import { ClientAddress } from "queries/types/entities/clients";

export const ADDRESS_TYPES = {
  MAIN: "main",
  BILLING: "billing",
  DELIVERY: "delivery",
  OTHER: "other",
} as const;

export type AddressTypeValue = (typeof ADDRESS_TYPES)[keyof typeof ADDRESS_TYPES];

type AddressTypeLike = string | { value?: string | null; label?: string | null } | null | undefined;

/**
 * The backend returns addressType values inconsistently cased ("main" vs "MAIN"),
 * so every comparison must go through here instead of comparing raw strings.
 */
export const normalizeAddressType = (addressType: AddressTypeLike): string => {
  if (!addressType) return "";

  const value = typeof addressType === "string" ? addressType : addressType.value;

  return value?.trim().toLowerCase() ?? "";
};

export const isAddressType = (addressType: AddressTypeLike, expected: AddressTypeLike): boolean => {
  const normalized = normalizeAddressType(addressType);

  return normalized !== "" && normalized === normalizeAddressType(expected);
};

export const isMainAddressType = (addressType: AddressTypeLike): boolean =>
  isAddressType(addressType, ADDRESS_TYPES.MAIN);

export const findAddressByType = <T extends Pick<ClientAddress, "addressType">>(
  addresses: T[] | undefined | null,
  expected: AddressTypeLike,
): T | undefined => addresses?.find(address => isAddressType(address.addressType, expected));

export const findMainAddress = <T extends Pick<ClientAddress, "addressType">>(
  addresses: T[] | undefined | null,
): T | undefined => findAddressByType(addresses, ADDRESS_TYPES.MAIN);

/**
 * Casing expected by each backend when addressType is SENT (mutation inputs and
 * query filters). Confirmed against the live APIs:
 *   - entities   -> uppercase ("MAIN")
 *   - central    -> lowercase ("main")
 *
 * Internally we always hold the lowercase value (see normalizeAddressType); this
 * is applied only at the edge, right before the value leaves the app.
 */
export type AddressTypeApi = "entities" | "central";

export const toBackendAddressType = (addressType: AddressTypeLike, api: AddressTypeApi): string => {
  const normalized = normalizeAddressType(addressType);

  if (!normalized) return "";

  return api === "entities" ? normalized.toUpperCase() : normalized;
};

export const toBackendAddressTypes = (
  addressTypes: AddressTypeLike[],
  api: AddressTypeApi,
): string[] => addressTypes.map(addressType => toBackendAddressType(addressType, api));
